import React, {
  forwardRef,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";

import { mapChildrenIndexedByType } from "../../helper/map-children";
import { useUniqueId } from "../hooks/useUniqueId";
import { ThemeContext } from "../Theme";
import TabList from "./TabList";
import Tab from "./Tab";
import TabPanel from "./TabPanel";
import { TabsContextProvider } from "./TabsContext";

import styles from "./Tabs.scss";

export const Tabs = forwardRef((props, forwardedRef) => {
  const {
    children,
    setIndex,
    scheme,
    customClass,
    onChange,
    ...restProps
  } = props;
  const [hideLeftTab, setHideLeftTab] = useState(true);
  const [hideRightTab, setHideRightTab] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(setIndex);
  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [controlledPosition, setControlledPosition] = useState(0);
  const theme = useContext(ThemeContext);
  const ref = useRef();
  const scrollInterval = useRef();
  const tabRefs = useRef([]);
  const controlledPositionRef = useRef();
  controlledPositionRef.current = controlledPosition;

  const setRef = (element) => {
    ref.current = element;
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
    }
  };

  const isDisabled = (tabIndex) =>
    tabRefs.current[tabIndex] &&
    tabRefs.current[tabIndex].current &&
    tabRefs.current[tabIndex].current.disabled;

  const addTabRef = (tabRef) => {
    tabRefs.current.push(tabRef);
  };

  const calcTabsWidth = () => {
    return tabRefs.current.reduce((tabsWidth, tabRef) => {
      if (!tabRef.current) {
        return tabsWidth;
      }
      return tabsWidth + tabRef.current.offsetWidth;
    }, 0);
  };

  const updateNavigationState = () => {
    if (!ref.current) {
      return;
    }
    const tabsWidth = calcTabsWidth();
    const tabsVisibleWidth = ref.current.offsetWidth;
    setHideLeftTab(controlledPositionRef.current >= 0);
    setHideRightTab(
      -controlledPositionRef.current + tabsVisibleWidth >= tabsWidth
    );
  };

  const statePosition = (e, position) => {
    setControlledPosition(position.x);
    updateNavigationState();
  };

  const moveToTab = (tabIndex) => {
    const tabRef = tabRefs.current[tabIndex];
    if (!tabRef || !tabRef.current) return;
    const tabOffset = tabRef.current.offsetLeft;
    const tabStart = tabOffset + controlledPositionRef.current;
    const tabsWidth = calcTabsWidth();
    const tabWidth = tabRef.current.offsetWidth;
    const tabEnd = tabStart + tabWidth;
    const tabsVisibleWidth = ref.current.offsetWidth;
    const requestAnimationFrame =
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame !== "undefined"
        ? window.requestAnimationFrame
        : (cb) => {
            cb();
          };
    if (tabStart < 0) {
      setControlledPosition(-tabOffset);
      requestAnimationFrame(updateNavigationState);
      return;
    }
    if (tabEnd > tabsVisibleWidth - 20) {
      let newX = -tabOffset;
      if (tabsWidth + newX < tabsVisibleWidth) {
        newX += tabsVisibleWidth - tabWidth;
      }
      setControlledPosition(newX);
      requestAnimationFrame(updateNavigationState);
    }
  };

  const moveToCurrent = () => {
    moveToTab(currentIndex);
  };

  const changeIndex = (index) => {
    // console.log("chang index", index);
    if (onChange) onChange(index);
    setCurrentIndex(index);
    moveToTab(index);
  };

  const changeTab = (key, index) => {
    let newIndex = index;

    switch (key) {
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = tabRefs.current.length - 1;
        break;
      case "ArrowLeft":
        newIndex = index - 1 >= 0 ? index - 1 : tabRefs.current.length - 1;
        break;
      case "ArrowRight":
        newIndex = index + 1 <= tabRefs.current.length - 1 ? index + 1 : 0;
        break;
      default:
        break;
    }

    switch (key) {
      case "Home":
        if (isDisabled(newIndex)) {
          for (let i = 0; i < tabRefs.current.length - 1; i += 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        break;
      case "End":
        if (isDisabled(newIndex)) {
          for (let i = newIndex; i > 0; i -= 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        break;
      case "ArrowLeft":
        if (isDisabled(newIndex)) {
          for (let i = newIndex; i >= 0; i -= 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        if (isDisabled(newIndex) && newIndex === 0) {
          for (let i = tabRefs.current.length - 1; i > 0; i -= 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        break;
      case "ArrowRight":
        if (isDisabled(newIndex)) {
          for (let i = newIndex + 1; i < tabRefs.current.length; i += 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        if (isDisabled(newIndex) && newIndex === tabRefs.current.length - 1) {
          for (let i = 0; i < tabRefs.current.length; i += 1) {
            if (isDisabled(i) === false) {
              newIndex = i;
              break;
            }
          }
        }
        break;
      default:
        break;
    }
    changeIndex(newIndex);
    if (tabRefs.current[newIndex] && tabRefs.current[newIndex].current) {
      tabRefs.current[newIndex].current.focus({ preventScroll: true });
    }
  };

  const stopScrolling = () => {
    setIsScrolling(false);
    clearInterval(scrollInterval.current);
  };

  const onRelease = () => {
    stopScrolling();
  };

  const onHold = (direction) => {
    if (!ref.current || isScrolling) {
      return;
    }
    const tabsWidth = calcTabsWidth();

    if (direction === "ArrowRight") {
      setHideLeftTab(false);
      if (tabsWidth - ref.current.offsetWidth > -controlledPosition.x) {
        setCurrentScrollIndex((scrollIndex) => scrollIndex + 1);
        setControlledPosition((x) => x - 5);
      }
    } else if (currentScrollIndex > 0) {
      setCurrentScrollIndex((scrollIndex) => scrollIndex - 1);
      setControlledPosition((x) => x + 5);
    }

    scrollInterval.current = setInterval(() => {
      if (!ref.current) {
        return;
      }
      if (direction === "ArrowRight") {
        if (
          tabsWidth - ref.current.offsetWidth >
          -controlledPositionRef.current
        ) {
          setControlledPosition((x) => x - 5);
        } else {
          setHideRightTab(true);
          stopScrolling();
        }
      } else if (controlledPositionRef.current < 0) {
        setHideRightTab(false);
        setControlledPosition((x) => x + 5);
      } else {
        setHideLeftTab(true);
        stopScrolling();
      }
    }, 50);

    setIsScrolling(true);
  };

  useEffect(() => {
    changeTab("ArrowRight", currentIndex - 1);
  }, []);

  const prevSetIndex = useRef();

  useEffect(() => {
    if (prevSetIndex.current !== setIndex) {
      prevSetIndex.current = setIndex;
      setCurrentIndex(setIndex);
    }
  });

  let classNames = scheme;
  if (Array.isArray(classNames)) {
    classNames = classNames.join(" ");
  }
  if (theme) {
    classNames += ` ${styles[theme]}`;
  }
  if (customClass) {
    classNames += ` ${customClass}`;
  }

  const tabsIndex = useUniqueId("Tabs", props);

  return (
    <div ref={setRef} className={classNames} {...restProps}>
      {mapChildrenIndexedByType(children, (child, childIndexByType) => (
        <TabsContextProvider
          value={{
            tabsId: `telekom-tabs-${tabsIndex + 1}`,
            childIndex: childIndexByType,
            addTabRef,
            changeIndex,
            changeTab,
            currentIndex,
            controlledPosition,
            statePosition,
            onHold,
            onRelease,
            hideRightTab,
            hideLeftTab,
            updateNavigationState,
            moveToCurrent,
          }}
        >
          {child}
        </TabsContextProvider>
      ))}
    </div>
  );
});

Tabs.displayName = "Tabs";

Tabs.propTypes = {
  scheme: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  customClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  onChange: PropTypes.func,
  setIndex: PropTypes.number,
};

Tabs.defaultProps = {
  scheme: styles.wrapper,
  customClass: undefined,
  onChange: undefined,
  setIndex: 0,
};

Tabs.Panel = TabPanel;

Tabs.List = TabList;

Tabs.Tab = Tab;

Tabs.Variant = {
  themeDark: "tabs--theme_dark",
  large: styles["tabs--large"],
};

export default Tabs;
